import { useEffect } from "react";
import SvgMapper from "../../features/common/SvgMapper";
import {
  getInterviewConfigLookups,
  createMixMatchInterview,
  getInterviewTypesByCategory,
} from "../../../application/services/interviewConfigService";
import { useDispatch, useSelector } from "react-redux";
import {
  setOpenModal,
  updateInterviewConfigData,
  updateInterviewConfigField,
  resetInterviewConfigField,
  setCreatedInterview,
} from "../../../store/interviewConfigReducer";
import BasicConfig from "./sections/BasicConfig";
import MixMatchParameters from "./sections/MixMatchParameters";
import CustomQuestions from "./sections/CustomQuestions";
import InterviewPreview from "./sections/InterviewPreview";
import InterviewDescription from "./sections/InterviewDescription";
import Footer from "../../features/common/Footer";
import useInterviewList from "../../hooks/useInterviewList";
import useMixMatchValidation from "./useMixMatchValidation";
import "../Style.css";

const isLookupFetched = { current: false };

const MixMatchComponents = () => {
  const dispatch = useDispatch();
  const { categoryOption, industryOption, interviewRoleOption, openModal, interviewTypeOption } = useSelector(
    (state) => state.interviewconfig
  );
  const configData = useSelector((state) => state.interviewconfig);

  const { fetchInterviewList } = useInterviewList();

  useEffect(() => {
    if (openModal) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [openModal]);


  useEffect(() => {
    if (!isLookupFetched.current) {
      isLookupFetched.current = true;
    const fetchLookups = async () => {
      try {
        const response = await getInterviewConfigLookups();
        const categories = response.category.data || [];
        dispatch(
          updateInterviewConfigData({
            categoryOption: categories,
            industryOption: response.industry_type.data || [],
            interviewRoleOption: response.interview_role.data || [],
          })
        );
      } catch (error) {
        console.error("Error fetching lookup Data:", error);
          isLookupFetched.current = false;
      }
    };
    fetchLookups();
    }
  }, [dispatch]);
  useEffect(() => {
    if (categoryOption && categoryOption.length > 0) {
      const currentCategoryName = configData.categoryName;
      const categoryExists = categoryOption.some(
        (cat) => cat.value?.toLowerCase() === currentCategoryName?.toLowerCase()
      );
      
      if (currentCategoryName && !categoryExists) {
        dispatch(updateInterviewConfigField({ 
          key: "categoryName", 
          value: "" 
        }));
      }
    }
  }, [categoryOption, configData.categoryName, dispatch]);

  const handleDurationChange = (e) => {
    const { value } = e.target;
    if (value === "") {
      dispatch(updateInterviewConfigField({ key: "duration", value: "" }));
      return;
    }

    const numericValue = Number(value);
    if (Number.isNaN(numericValue)) {
      return;
    }

    if (numericValue >= 0 && numericValue <= 120) {
      dispatch(
        updateInterviewConfigField({ key: "duration", value: numericValue })
      );
    }
  };

  const onclose = () => {
    dispatch(setOpenModal(false));
    dispatch(resetInterviewConfigField());
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    dispatch(updateInterviewConfigField({ key: name, value }));
  };

  const onCategoryChange = (e) => {
    const selected = categoryOption.find(
      (cat) => cat.value === e.target.value
    );

    dispatch(updateInterviewConfigField({ key: "categoryName", value: selected.value }));
    dispatch(updateInterviewConfigField({ key: "categoryId", value: selected.id }));

    dispatch(updateInterviewConfigField({ key: "interviewType", value: "" }));
    dispatch(updateInterviewConfigData({ interviewTypeOption: [] }));
  };

  useEffect(() => {
  const fetchInterviewTypes = async () => {
    const { categoryId } = configData;

    if (!categoryId) return;

    try {
      const response = await getInterviewTypesByCategory(categoryId);
      const types = response.data || [];

      dispatch(updateInterviewConfigData({ interviewTypeOption: types }));

      // auto select first
      if (types.length > 0) {
        dispatch(
          updateInterviewConfigField({
            key: "interviewType",
            value: types[0].id,
          })
        );
      }else{
        dispatch(updateInterviewConfigField({ key: "interviewType", value: "" }));
      }
    } catch (error) {
      dispatch(updateInterviewConfigData({ interviewTypeOption: [] }));
      dispatch(updateInterviewConfigField({ key: "interviewType", value: "" }));
    }
  };

  fetchInterviewTypes();
}, [configData.categoryId, dispatch, ]);

  const {
    formErrors,
    isCreateDisabled,
    lookupId,
    setFormErrors,
    validateForm,
  } = useMixMatchValidation(configData, categoryOption);

  const onCreate = async () => {
    try {
      if (!validateForm() || isCreateDisabled) {
        return;
      }
      const {
        additionalData,
        interviewType,
        businessType,
        difficulty,
        duration,
        industryType,
        interviewName,
        interviewRole,
        interviewTone,
        jobDescription,
        organizationType,
        rubric_url,
      } = configData;

      const payload = {
        interview_name: interviewName?.trim(),
        category: lookupId,
        interview_type_id: interviewType || null,
        difficulty,
        duration,
        industry_type: industryType ? industryType : null,
        organization_type: organizationType || "",
        business_type: businessType || "",
        interview_role: interviewRole ? interviewRole : null,
        interview_tone: interviewTone,
        is_question_generated_using_ai: additionalData?.length > 0 ? false : true,
        additonal_data: additionalData || "",
        job_description: jobDescription || "",
        description: "",
        rubric_url: rubric_url || "",
        status: "INACTIVE",
      };

      const response = await createMixMatchInterview(payload);
      setFormErrors([]);
      dispatch(resetInterviewConfigField());
      dispatch(setCreatedInterview(response?.data));

      await fetchInterviewList();
      dispatch(setOpenModal(false));
    } catch (error) {
      console.error("Error creating interview:", error);
    }
  };

  return (
    <div className="mix-match-wrapper">
      <main className="mix-match-main">
        <div className="mix-match-container">
          <div 
            className="mix-match-modal-backdrop"
          >
            <div 
              className="mix-match-modal-container custom-scrollbar"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mix-match-modal-header">
                <div className="mix-match-modal-header-content">
                  <div className="mix-match-modal-header-left">
                    <div className="mix-match-modal-icon-container">
                      {SvgMapper.mixMatch("w-5 h-5 text-blue-600")}
                    </div>
                    <div>
                      <h2 className="mix-match-modal-title">
                        Mix & Match Interview Builder
                      </h2>
                      <p className="mix-match-modal-subtitle">
                        Create custom interviews with specific combinations
                      </p>
                    </div>
                  </div>
                  <button
                    className="mix-match-modal-close-button"
                    onClick={() => onclose()}
                  >
                    {SvgMapper.closeIcon("w-6 h-6")}
                  </button>
                </div>
              </div>

              <form className="mix-match-form">
                {formErrors.length > 0 && (
                  <div className="mix-match-error-container">
                    <ul className="mix-match-error-list">
                      {formErrors.map((error) => (
                        <li key={error}>{error}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <BasicConfig
                  categoryOption={categoryOption}
                  onCategoryChange={onCategoryChange}
                  interviewTypeOption={interviewTypeOption}
                  onDurationChange={handleDurationChange}
                  onChange={onChange}
                />
                <MixMatchParameters
                  industryOption={industryOption}
                  onChange={onChange}
                  interviewRoleOption={interviewRoleOption}
                />
                <InterviewDescription onChange={onChange} />
                <CustomQuestions onChange={onChange} />
                <InterviewPreview />
                <Footer onCreateInterview={onCreate} isDisabled={isCreateDisabled} />
              </form>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default MixMatchComponents;
